import clsx from 'clsx'
import { ButtonCommon, IconWrap, InputCommon, PageHeader } from '../../components'
import styles from './index.module.scss'
import headerImg from '../../assets/images/buildingHeader.png'
import { Upload, UploadProps } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useCallback, useState } from 'react'
import { CardBuilding } from './CardBuilding'
import { SearchIcon } from '../../assets/icons'
import { DataEmpty } from '../../components/DataEmpty'
import { IBuilding } from '../../models'
import { useGetListBuilding, useImportBuilding } from '../../hooks/query/building'
import debounce from 'lodash.debounce'
import { Loading } from '../../components/Loading'
import { RcFile } from 'antd/es/upload'
import { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface'
import { PaginationCommon } from '../../components/Pagination'
import { openNotificationWithIcon } from '../../hooks'
import { SIZE_5MB } from '../../until/constant'
import { useTranslation } from 'react-i18next'

export const Building = () => {
  const [isLoadingUpload, setIsLoadingUpload] = useState(false)
  const [loadingImg, setLoadingImg] = useState(true)
  const [params, setParams] = useState({
    page_size: 60,
    page: 1,
    text_search: '',
  })
  const { data, isLoading, isFetching } = useGetListBuilding(params)
  const {t} = useTranslation()
  const onChangePagination = (page: number, page_size: number) => {
    if (page_size !== params.page_size) {
      return setParams({ ...params, page: 1, page_size })
    }
    setParams({ ...params, page, page_size })
  }
  const onHandleSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    debouncedSearch(e.target.value)
  }

  const debouncedSearch = useCallback(
    debounce((nextValue) => setParams((prev: any) => ({ ...prev, text_search: nextValue, page: 1 })), 500),
    [],
  )

  const mutateImportCsv = useImportBuilding()
  const props: UploadProps = {
    name: 'file',
    showUploadList: false,
    accept: 'text/csv',
    headers: {
      authorization: 'authorization-text',
    },

    beforeUpload(file: RcFile) {
      const isCSV = file.type === 'text/csv'
      const isLt5M = file.size < SIZE_5MB
      if (!isCSV) {
        openNotificationWithIcon('error', '', `${t('building.message.invalidCsv')}\n${t('building.message.invalidCsv2')}`)
        return isCSV
      }
      if (!isLt5M) {
        openNotificationWithIcon('error', '', t('building.message.fileLimit'))
        return isLt5M
      }
    },
    onChange(info) {
      if (info.file.status === 'done') {
        openNotificationWithIcon('success', '', `${info.file.name} ${t('building.message.upLoadSuccess')}`)
      } else if (info.file.status === 'error') {
        openNotificationWithIcon('error', '', `${info.file.name} ${t('building.message.upLoadFail')}`)
      }
    },
    customRequest: async (options: RcCustomRequestOptions) => {
      const { file } = options
      const fmData = new FormData()
      fmData.append('file', file)
      try {
        setIsLoadingUpload(true)
        const data = await mutateImportCsv.mutateAsync(fmData)
        openNotificationWithIcon('success', '', data.data)
      } catch (err: any) {
        openNotificationWithIcon('error', '', err.message)
      }
      setIsLoadingUpload(false)
    },
  }

  const handleOnLoadImg = () => {
    setLoadingImg(false)
  }

  return (
    <div className={styles.building}>
      <PageHeader title={t('building.buildingList')} backgroundImage={headerImg} height={'108px'}>
        <div className={styles.building__header}>
          <InputCommon
            suffix={<IconWrap icon={<SearchIcon />} />}
            placeholder={t('building.search')}
            className={styles.building__header__search__input}
            onChange={onHandleSearch}
          />
          <Upload {...props}>
            <ButtonCommon icon={<UploadOutlined />} className={styles.building__header__button} type="primary">
              {t('building.importCSV')}
            </ButtonCommon>
          </Upload>
        </div>
      </PageHeader>
      <Loading isLoading={isLoading || isLoadingUpload || isFetching} />
      {isLoading && loadingImg ? null : (
        <div className={clsx(styles.building__content, 'main-content')}>
          {data?.data && data.data.length > 0 ? (
            <>
              <div className={styles.building__wrap__item}>
                {data.data.map((item: IBuilding, index: number) => {
                  return <CardBuilding building={item} key={index} />
                })}
              </div>
              <PaginationCommon
                defaultCurrent={1}
                total={data.pagination.total}
                pageSize={data.pagination.per_page}
                current={data.pagination.current_page}
                onChangePagination={onChangePagination}
                pageSizeOptions={['6', '12', '30', '60']}
                showSizeChanger
              />
            </>
          ) : (
            <DataEmpty detail={t('building.noResultsMessage')} onLoadImg={handleOnLoadImg}>
              <Upload {...props}>
                <ButtonCommon icon={<UploadOutlined />} type="primary">
                  {t('building.importCSV')}
                </ButtonCommon>
              </Upload>
            </DataEmpty>
          )}
        </div>
      )}
    </div>
  )
}
